/**
 * Get the assumed speed (m/s) for a sport type from sportsSpeeds.
 * Supports both number (legacy) and zone-based object formats.
 * - If value is a number: use that speed for all zones
 * - If value is an object: use zone-specific speed (e.g., {1: 2, 2: 2.5, 3: 3, ...})
 * Returns the speed from sportsSpeeds if available, otherwise falls back to default speed (3 m/s for Run).
 * If speed is <= 0, returns 0 (meaning distance should not be inferred for this sport).
 */
import { DEFAULT_SPORT_SPEEDS } from './constants';

const getDefaultSportSpeed = (sportType?: string): number => {
  if (!sportType) {
    return 0;
  }
  const entry = (DEFAULT_SPORT_SPEEDS as Record<string, any>)[sportType];
  if (typeof entry === 'number') {
    return entry;
  }
  if (typeof entry === 'object' && entry !== null) {
    if (typeof entry['3'] === 'number') {
      return entry['3'];
    }
    const firstZoneSpeed = Object.values(entry).find(
      (value) => typeof value === 'number'
    );
    if (typeof firstZoneSpeed === 'number') {
      return firstZoneSpeed;
    }
  }
  return 0;
};

export const getSportSpeed = (
  sportType: string,
  sportsSpeeds:
    | Record<string, number | Record<string, number>>
    | null
    | undefined,
  zone: number = 0
): number => {
  // Default fallback speed is zero when we lack sport-specific data
  const defaultSpeed = getDefaultSportSpeed(sportType);

  if (!sportsSpeeds || !sportType) {
    return defaultSpeed;
  }

  const speed = sportsSpeeds[sportType];

  // If speed is a number (legacy format), use it for all zones
  if (typeof speed === 'number') {
    return speed;
  }

  // If speed is an object (zone-based format)
  if (typeof speed === 'object' && speed !== null) {
    // Convert zone to string key (zones are stored as string keys in JSON)
    const zoneKey = zone > 0 ? String(zone) : '3'; // Default to zone 3 if no zone specified
    const zoneSpeed = speed[zoneKey];

    // If zone-specific speed exists, use it
    if (typeof zoneSpeed === 'number') {
      return zoneSpeed;
    }

    // Fallback: if zone not found, use zone 2 (default zone) or first available zone
    if (typeof speed['2'] === 'number') {
      return speed['2'];
    }

    // Try to find any zone speed
    const firstZoneSpeed = Object.values(speed).find(
      (v) => typeof v === 'number'
    );
    if (typeof firstZoneSpeed === 'number') {
      return firstZoneSpeed;
    }
  }

  return defaultSpeed;
};

export const normalizeWorkoutSteps = (steps: unknown): string[] => {
  if (!steps) {
    return [];
  }
  if (Array.isArray(steps)) {
    return steps.filter((item): item is string => typeof item === 'string');
  }
  if (typeof steps === 'string') {
    const trimmed = steps.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (item): item is string => typeof item === 'string'
        );
      }
    } catch {
      const fragments = trimmed
        .split(/[;,\\n\\r]+/)
        .map((fragment) => fragment.trim())
        .filter((fragment) => fragment.length > 0);
      return fragments;
    }
  }
  return [];
};

export const parseWorkoutData = (
  steps: string[],
  sportType: string = 'Run',
  sportsSpeeds?: Record<string, number | Record<string, number>> | null
) => {
  if (!steps) return [];
  let time = 0;

  let workoutData: { time: number; zone: number }[] = [];

  for (const step of steps) {
    const topLevelSteps = splitTopLevelSteps(step);
    for (const topLevelStep of topLevelSteps) {
      const repeatedSteps = parseRepeatedSteps(topLevelStep);
      for (const repeatedStep of repeatedSteps) {
        const zone = parseStepZone(repeatedStep) || 2;
        const sportSpeed = getSportSpeed(sportType, sportsSpeeds, zone);

        let stepTime = parseStepTime(repeatedStep);
        let stepDistance = parseStepDistance(repeatedStep);

        // Allow if we have at least one of time or distance
        if (!stepTime && !stepDistance) {
          if (sportType !== 'WeightTraining') {
            console.warn('No time or distance found in step', repeatedStep);
          }
          continue;
        }

        // Calculate missing value from the one we have
        if (!stepDistance && stepTime) {
          stepDistance =
            sportSpeed <= 0 ? 0 : Math.round(stepTime * sportSpeed * 10) / 10;
        } else if (!stepTime && stepDistance) {
          stepTime =
            sportSpeed <= 0 ? 0 : Math.round(stepDistance / sportSpeed);
        }

        workoutData.push({ time, zone });
        time += stepTime;
      }
    }
  }

  workoutData = [{ time: 0, zone: 0 }, ...workoutData, { time, zone: 0 }];
  return workoutData;
};

export const parseRepeatedSteps = (step: string): string[] => {
  const repeatsMatch = step.match(/(\d+)\s*x\s*\(([^)]+)\)/);
  if (!repeatsMatch) {
    const simpleMatch = step.match(/(\d+)\s*x\s*([^\s]+)/);
    if (simpleMatch) {
      const repeats = parseInt(simpleMatch[1]);
      const stepValue = simpleMatch[2];
      return Array.from({ length: repeats }, () => stepValue);
    }
    return [step];
  }

  const repeats = parseInt(repeatsMatch[1]);
  const steps = repeatsMatch[2].split(/\s*[+,]\s*/);
  return Array.from({ length: repeats }, () => steps).flat();
};

export type StrengthWorkoutStep = {
  repeatCount: number;
  exerciseCategory: string;
  exerciseName: string;
  reps: number;
  weightValue: number;
  weightDisplayUnit: 'KILOGRAM' | 'POUND';
  restDurationSeconds: number;
};

export const parseStrengthWorkoutStep = (
  line: string
): StrengthWorkoutStep | null => {
  const outer = line.match(/^(\d+)x\((.+)\)$/i);
  if (!outer) return null;

  const repeatCount = Number(outer[1]) || 1;
  const body = outer[2];
  const [setPart, restPart] = body.split(/\s*\+\s*rest\s*/i);
  if (!setPart || !restPart) return null;

  const [exerciseCategoryRaw, exerciseDetailsRaw] = setPart.split(/:\s*/);
  if (!exerciseCategoryRaw || !exerciseDetailsRaw) return null;

  const [exerciseNameRaw, repsWeightRaw] = exerciseDetailsRaw.split(/\s*x\s*/i);
  if (!exerciseNameRaw || !repsWeightRaw) return null;

  const [repsRaw, weightRaw] = repsWeightRaw.split(/\s*@\s*/i);
  if (!repsRaw || !weightRaw) return null;

  const [weightValueRaw, unitRaw] = weightRaw.trim().split(/\s+/);
  const [restValueRaw, restUnitRaw] = restPart.trim().split(/\s+/);
  if (!weightValueRaw || !unitRaw || !restValueRaw || !restUnitRaw) return null;

  return {
    repeatCount,
    exerciseCategory: exerciseCategoryRaw.trim(),
    exerciseName: exerciseNameRaw.trim(),
    reps: Number(repsRaw) || 1,
    weightValue: Number(weightValueRaw) || 0,
    weightDisplayUnit: unitRaw.toLowerCase() === 'kg' ? 'KILOGRAM' : 'POUND',
    restDurationSeconds:
      restUnitRaw.toLowerCase() === 'min'
        ? Number(restValueRaw) * 60
        : Number(restValueRaw),
  };
};

export type StrengthWorkoutRow = StrengthWorkoutStep & { id?: string };

export const parseStrengthWorkoutRowText = (
  line: string
): StrengthWorkoutStep | null => parseStrengthWorkoutStep(line);

export const calculateStrengthDurationSeconds = (steps: string[]) => {
  const duration = steps.reduce((total, step) => {
    const parsed = parseStrengthWorkoutStep(step);
    if (!parsed) {
      return total + 60;
    }
    return (
      total +
      parsed.repeatCount * (parsed.reps * 5 + parsed.restDurationSeconds)
    );
  }, 0);

  return duration > 0 ? duration : Math.max(60, steps.length * 60);
};

const splitTopLevelSteps = (step: string): string[] => {
  const chunks: string[] = [];
  let buf = '';
  let depth = 0;
  for (const char of step) {
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    }
    if (depth === 0 && char === ',') {
      const token = buf.trim();
      if (token) {
        chunks.push(token);
      }
      buf = '';
      continue;
    }
    buf += char;
  }
  const lastToken = buf.trim();
  if (lastToken) {
    chunks.push(lastToken);
  }
  return chunks.length > 0 ? chunks : [step];
};

export const parseStepZone = (step: string): number => {
  const zoneMatch = step.match(/(^|\s)(?:Z|zone)\s*[:\-]?\s*(\d+)/i);
  return zoneMatch ? parseInt(zoneMatch[2]) : 0;
};

export const parseStepTime = (step: string): number => {
  const timeMatch = step.match(
    /(\d+(\.\d+)?)(s|sec|second|seconds|secs|h|hour|hours|min|mins|minute|minutes)(\s+|$)/i
  );
  if (!timeMatch) return 0;
  let time = null;
  switch (timeMatch[3].toLowerCase()) {
    case 's':
    case 'sec':
    case 'second':
    case 'seconds':
    case 'secs': {
      time = parseFloat(timeMatch[1]);
      break;
    }
    case 'min':
    case 'mins':
    case 'minute':
    case 'minutes': {
      time = parseFloat(timeMatch[1]) * 60;
      break;
    }
    case 'h':
    case 'hour':
    case 'hours': {
      time = parseFloat(timeMatch[1]) * 3600;
      break;
    }
  }
  if (time) time = Math.round(time);
  return time || 0;
};

export const parseStepDistance = (step: string): number | null => {
  const distanceMatch = step.match(
    /(\d+(\.\d+)?)(?:\s*)(m|meter|meters|km|mi|mile|miles)(\s+|$)/i
  );
  if (!distanceMatch) return null;
  let distance = null;
  const unit = distanceMatch[3].toLowerCase();
  switch (unit) {
    case 'm':
    case 'meter':
    case 'meters': {
      distance = parseFloat(distanceMatch[1]);
      break;
    }
    case 'km': {
      distance = parseFloat(distanceMatch[1]) * 1000;
      break;
    }
    case 'mi':
    case 'mile':
    case 'miles': {
      distance = parseFloat(distanceMatch[1]) * 1609.34;
      break;
    }
  }
  if (distance) distance = Math.round(distance * 10) / 10;
  return distance;
};

const GARMIN_UNIT_REGEX = /(\d+(?:\.\d+)?)m(?![a-zA-Z])/gi;

export const normalizeGarminUnitTokens = (step: string): string => {
  // Guard against undefined or non-string input
  if (!step || typeof step !== 'string') {
    return String(step || '');
  }
  return step.replace(GARMIN_UNIT_REGEX, (match, numberPartStr) => {
    const numberPart = parseFloat(numberPartStr);

    // If the number is a multiple of 100, assume meters (e.g., 100m, 200m)
    // Otherwise, assume minutes (e.g., 1m, 5m, 30m)
    if (numberPart > 99) {
      return `${numberPartStr}meter`;
    } else {
      return `${numberPartStr}min`;
    }
  });
};

export const workoutTotalDuration = (
  steps: string[],
  sportType: string = 'Run',
  sportsSpeeds?: Record<string, number | Record<string, number>> | null
): number | null => {
  let totalDuration = 0;

  for (const step of steps) {
    const topLevelSteps = splitTopLevelSteps(step);
    for (const topLevelStep of topLevelSteps) {
      const stepsList = parseRepeatedSteps(topLevelStep);
      for (const repeatedStep of stepsList) {
        const zone = parseStepZone(repeatedStep) || 2;
        const sportSpeed = getSportSpeed(sportType, sportsSpeeds, zone);

        const distance = parseStepDistance(repeatedStep);
        const time = parseStepTime(repeatedStep);

        if (time) {
          totalDuration += time;
        } else if (distance) {
          if (sportSpeed <= 0) {
            continue;
          } else {
            totalDuration += distance / sportSpeed;
          }
        }
      }
    }
  }
  return totalDuration === 0 ? null : totalDuration;
};

export const workoutTotalDistance = (
  steps: string[],
  sportType: string = 'Run',
  sportsSpeeds?: Record<string, number | Record<string, number>> | null
): number | null => {
  let totalDistance = 0;

  for (const step of steps) {
    const topLevelSteps = splitTopLevelSteps(step);
    for (const topLevelStep of topLevelSteps) {
      const stepsList = parseRepeatedSteps(topLevelStep);
      for (const repeatedStep of stepsList) {
        const zone = parseStepZone(repeatedStep) || 2;
        const sportSpeed = getSportSpeed(sportType, sportsSpeeds, zone);

        const distance = parseStepDistance(repeatedStep);
        const time = parseStepTime(repeatedStep);

        if (distance !== null && distance > 0) {
          totalDistance += distance;
        } else if (time > 0 && sportSpeed > 0) {
          totalDistance += time * sportSpeed;
        }
      }
    }
  }

  return totalDistance === 0 ? null : Math.round(totalDistance * 10) / 10;
};

export const stepToGarminIntensity = (step: string) => {
  const zones = ['REST', 'RECOVERY', 'ACTIVE', 'INTERVAL', 'COOLDOWN'];
  const zoneIndex = parseStepZone(step);
  const clampedIndex = Math.max(0, Math.min(zones.length - 1, zoneIndex));
  return zones[clampedIndex];
};
